import { MARKET_SAVINGS_PRICE_BREAK_QUANTITIES, MARKET_SAVINGS_PRICE_BREAKS } from './priceBreaks';
import type {
    MarketSavingsDetailEvidence,
    MarketSavingsDetailOffer,
    MarketSavingsReportRow,
    ReportMetricId,
} from './schemas';
import { getStandardPriceSnapshotSelection } from './standardPriceSnapshot';

export type MarketSavingsDetailRankedCandidate = {
    ipn: string;
    score: number;
    row: MarketSavingsReportRow;
};

/**
 * Version 1 detail policy: rank each IPN by its maximum positive opportunity over every
 * distinct ordered enabled-metric pair, then by IPN. It is deliberately scenario-neutral.
 */
export function selectMarketSavingsDetailCandidates(
    rows: readonly MarketSavingsReportRow[],
    enabledMetrics: readonly ReportMetricId[],
    limit: number,
): MarketSavingsDetailRankedCandidate[] {
    return mergeMarketSavingsDetailCandidates([], rankMarketSavingsDetailCandidates(rows, enabledMetrics), limit);
}

/** Scores `rows`, dropping the ones with no positive opportunity. Order is not significant. */
export function rankMarketSavingsDetailCandidates(
    rows: readonly MarketSavingsReportRow[],
    enabledMetrics: readonly ReportMetricId[],
): MarketSavingsDetailRankedCandidate[] {
    return rows.flatMap((row): MarketSavingsDetailRankedCandidate[] => {
        if (!isPositive(row.demandQuantity)) return [];
        let score = 0;
        for (const baseline of enabledMetrics) {
            for (const comparison of enabledMetrics) {
                if (baseline === comparison) continue;
                const baselinePrice = row.metrics[baseline];
                const comparisonPrice = row.metrics[comparison];
                if (!isPositive(baselinePrice) || !isPositive(comparisonPrice)) continue;
                const opportunity = (comparisonPrice - baselinePrice) * row.demandQuantity;
                if (Number.isFinite(opportunity)) score = Math.max(score, opportunity);
            }
        }
        return isPositive(score) ? [{ ipn: row.ipn, score, row }] : [];
    });
}

/**
 * Keeps the best `limit` of `ranked` and `candidates` combined. The comparator is a total
 * order over distinct IPNs, so merging batch by batch yields the same result as ranking
 * every row at once.
 */
export function mergeMarketSavingsDetailCandidates(
    ranked: readonly MarketSavingsDetailRankedCandidate[],
    candidates: readonly MarketSavingsDetailRankedCandidate[],
    limit: number,
): MarketSavingsDetailRankedCandidate[] {
    return [...ranked, ...candidates]
        .sort((left, right) => right.score - left.score || left.ipn.localeCompare(right.ipn))
        .slice(0, Math.max(0, limit));
}

type DetailPriceBreakSelection = {
    offerId: string;
    quantity: number;
    unitPrice: number;
    role: 'population' | 'selected-source';
};

/**
 * Returns the exact offer breaks used as evidence for one report metric. The evidence was stored for
 * all demand; a scenario using other demand passes that demand and the price it compares, so the
 * offers are taken at the break that price came from.
 */
export function selectDetailPriceBreaksForMetric({
    evidence,
    metric,
    demandQuantity = evidence.demandQuantity,
    metricValue = evidence.metricValues[metric],
}: {
    evidence: MarketSavingsDetailEvidence;
    metric: ReportMetricId;
    demandQuantity?: number | null;
    metricValue?: number | null;
}): DetailPriceBreakSelection[] {
    if (
        metric === 'marketP50Price' ||
        metric === 'marketP10Price' ||
        metric === 'marketP1Price' ||
        metric === 'demandQualifiedMarketMinPrice'
    ) {
        return deduplicateOffers(evidence.marketOffers).flatMap((offer) => {
            const priceBreak = demandQualifiedBreak(offer, demandQuantity, 'market');
            return priceBreak
                ? [toSelection(offer, priceBreak, approximatelyEqual(priceBreak.unitPrice, metricValue))]
                : [];
        });
    }

    const snapshot = metric === 'latestContractPrice' ? evidence.contractPriceSnapshot : evidence.standardPriceSnapshot;
    if ((metric === 'latestStandardPrice' || metric === 'latestContractPrice') && snapshot) {
        const selected = getStandardPriceSnapshotSelection(snapshot, demandQuantity);
        return selected && approximatelyEqual(selected.unitPrice, metricValue)
            ? [{ ...selected, role: 'selected-source' }]
            : [];
    }

    // Contract prices have no legacy live-evidence fallback.
    if (metric === 'latestContractPrice') return [];

    if (isPositive(demandQuantity)) {
        return selectInternalSourceAtDemand(evidence, metric, demandQuantity);
    }

    const offers =
        metric === 'lastPurchasePrice' || metric === 'cheapestPurchasePrice'
            ? deduplicateOffers(evidence.purchaseOffers)
            : deduplicateOffers(evidence.standardPriceOffers);
    if (metric === 'lastPurchasePrice') {
        const latestDate = offers.reduce<string | undefined>(
            (latest, offer) => (!latest || (offer.date ?? '') > latest ? offer.date : latest),
            undefined,
        );
        const latestOffers = offers.filter((offer) => offer.date === latestDate || (!latestDate && !offer.date));
        const selected = minimumBreak(latestOffers);
        return selected ? [toSelection(selected.offer, selected.priceBreak, true)] : [];
    }
    const selected = minimumBreak(offers);
    return selected ? [toSelection(selected.offer, selected.priceBreak, true)] : [];
}

export function deduplicateOffers(offers: readonly MarketSavingsDetailOffer[]): MarketSavingsDetailOffer[] {
    const result = new Map<string, MarketSavingsDetailOffer>();
    for (const offer of offers) result.set(`${offer.provenance.source}:${offer.id}`, offer);
    return [...result.values()];
}

type PriceBreak = MarketSavingsDetailOffer['priceBreaks'][number];

/**
 * The internal price at a demand, among offers priced at the demand's break: the cheapest purchase
 * price, the cheapest on the latest purchase date, or the latest standard price.
 */
function selectInternalSourceAtDemand(
    evidence: MarketSavingsDetailEvidence,
    metric: ReportMetricId,
    demand: number,
): DetailPriceBreakSelection[] {
    const isStandard = metric === 'latestStandardPrice';
    const offers = deduplicateOffers(isStandard ? evidence.standardPriceOffers : evidence.purchaseOffers);
    const priced = offers.flatMap((offer) => {
        const priceBreak = demandQualifiedBreak(offer, demand, isStandard ? 'market' : 'purchase');
        return priceBreak ? [{ offer, priceBreak }] : [];
    });
    const latestDate = priced.reduce(
        (latest, { offer }) => ((offer.date ?? '') > latest ? (offer.date ?? '') : latest),
        '',
    );
    const candidates =
        metric === 'lastPurchasePrice' ? priced.filter(({ offer }) => (offer.date ?? '') === latestDate) : priced;
    const selected = isStandard
        ? candidates.sort(
              (left, right) =>
                  (right.offer.date ?? '').localeCompare(left.offer.date ?? '') ||
                  left.offer.id.localeCompare(right.offer.id),
          )[0]
        : candidates.sort(
              (left, right) =>
                  left.priceBreak.unitPrice - right.priceBreak.unitPrice || left.offer.id.localeCompare(right.offer.id),
          )[0];
    return selected ? [toSelection(selected.offer, selected.priceBreak, true)] : [];
}

/**
 * The break ingestion stores an offer's price under for each canonical break, as `price_breaks.rs` in
 * historical-offer-consumer does: the last break whose MOQ the canonical quantity reaches, kept for a
 * purchase price only at the MOQ's next canonical break, and for any other price only when the MOQ is
 * at least half the canonical quantity.
 */
function canonicalBreaks(
    offer: MarketSavingsDetailOffer,
    tiering: 'purchase' | 'market',
): Array<{ quantity: number; priceBreak: PriceBreak }> {
    const byQuantity = [...offer.priceBreaks].sort((left, right) => left.quantity - right.quantity);
    return MARKET_SAVINGS_PRICE_BREAKS.flatMap((canonical) => {
        const quantity = MARKET_SAVINGS_PRICE_BREAK_QUANTITIES[canonical];
        const priceBreak = byQuantity.filter((candidate) => candidate.quantity <= quantity).at(-1);
        if (!priceBreak || !(priceBreak.unitPrice > 0)) return [];
        const kept =
            tiering === 'purchase'
                ? nextCanonicalQuantity(priceBreak.quantity) === quantity
                : priceBreak.quantity >= quantity * 0.5;
        return kept ? [{ quantity, priceBreak }] : [];
    });
}

function nextCanonicalQuantity(moq: number): number {
    const quantities = MARKET_SAVINGS_PRICE_BREAKS.map((canonical) => MARKET_SAVINGS_PRICE_BREAK_QUANTITIES[canonical]);
    return quantities.find((quantity) => moq <= quantity) ?? MARKET_SAVINGS_PRICE_BREAK_QUANTITIES['50k'];
}

/**
 * The break the metrics price an offer at: its largest stored canonical price the demand reaches, as
 * `demandQualifiedPriceSql` in monitoring-service does. An offer with none of them is not priced. Without
 * demand nothing is priced, so market offers are shown at their 1k break.
 */
function demandQualifiedBreak(
    offer: MarketSavingsDetailOffer,
    demand: number | null,
    tiering: 'purchase' | 'market',
): PriceBreak | undefined {
    if (!isPositive(demand)) return offer.priceBreaks.find((priceBreak) => priceBreak.quantity === 1_000);
    return canonicalBreaks(offer, tiering)
        .filter(({ quantity }) => quantity <= demand)
        .at(-1)?.priceBreak;
}

function minimumBreak(
    offers: readonly MarketSavingsDetailOffer[],
): { offer: MarketSavingsDetailOffer; priceBreak: MarketSavingsDetailOffer['priceBreaks'][number] } | undefined {
    return offers
        .flatMap((offer) => offer.priceBreaks.map((priceBreak) => ({ offer, priceBreak })))
        .sort(
            (left, right) =>
                left.priceBreak.unitPrice - right.priceBreak.unitPrice ||
                left.priceBreak.quantity - right.priceBreak.quantity ||
                left.offer.id.localeCompare(right.offer.id),
        )[0];
}

function toSelection(
    offer: MarketSavingsDetailOffer,
    priceBreak: MarketSavingsDetailOffer['priceBreaks'][number],
    selected: boolean,
): DetailPriceBreakSelection {
    return {
        offerId: offer.id,
        quantity: priceBreak.quantity,
        unitPrice: priceBreak.unitPrice,
        role: selected ? 'selected-source' : 'population',
    };
}

function approximatelyEqual(left: number, right: number | null | undefined): boolean {
    return typeof right === 'number' && Math.abs(left - right) <= Math.max(1, Math.abs(right)) * 1e-9;
}

function isPositive(value: number | null | undefined): value is number {
    return typeof value === 'number' && Number.isFinite(value) && value > 0;
}
